import { defineStore } from 'pinia'
import { ref } from 'vue'

// Import company APIs
import { 
  getTalentPools as getCompanyTalentPools,
  getTalentPool as getCompanyTalentPoolById,
  createTalentPool as createCompanyTalentPool,
  getWorkTypes as getCompanyWorkTypes,
  getMembers as getCompanyTalentPoolMembers,
  getMemberResume as getCompanyMemberResume
} from '@/api/company/talentPools'

// Import contractor APIs
import {
  getTalentPools as getContractorTalentPools,
  getTalentPool as getContractorTalentPoolById,
  applyToTalentPool as contractorApply,
  withdrawApplication as contractorWithdraw

} from '@/api/contractor/talentPools'

export const useTalentPoolsStore = defineStore('talentPools', () => {
  // State
  const talentPools = ref([])
  const members = ref([])
  const workTypes = ref([])
  const isLoading = ref(false)
  const actionLoadingId = ref(null)
  const activePool = ref(null)
  const isLoadingActivePool = ref(false)
  const isLoadingMembers = ref(false)
  const error = ref(null)

  // Action that accepts the role as an argument
  const fetchTalentPools = async (role, options = {}) => {
    const { skipIfLoaded = false, silent = false } = options

    if (skipIfLoaded && talentPools.value.length) return

    if (!silent) {
      isLoading.value = true
    }
    error.value = null

    try {
      let response
      if (role === 'company') {
        response = await getCompanyTalentPools()
      } else if (role === 'contractor') {
        response = await getContractorTalentPools()
      } else {
        throw new Error('Invalid user role provided.')
      }

      talentPools.value = response.data.data
    } catch (err) {
      error.value = 'Failed to fetch talent pools.'
      console.error(err)
    } finally {
      if (!silent) {
        isLoading.value = false
      }
    }
  }

  const fetchTalentPoolById = async (talentPoolId, role) => {
    const cached = talentPools.value.find(p => p.id === Number(talentPoolId));
    if (cached) {
      activePool.value = cached;
    } else {
      activePool.value = null;
    }
    isLoadingActivePool.value = true

    try {
      let response
      if (role === 'company') {
        response = await getCompanyTalentPoolById(talentPoolId)
      } else if (role === 'contractor') {
        response = await getContractorTalentPoolById(talentPoolId) 
      } else {
        throw new Error('Invalid user role provided.')
      }

      activePool.value = response.data
    } catch (err) {
      error.value = 'Failed to fetch talent pool details.'
      console.error(err)
    } finally {
      isLoadingActivePool.value = false
    }
  }

  const fetchWorkTypes = async () => {
    const response = await getCompanyWorkTypes()

    // Assign the data to your ref
    workTypes.value = response.data.data
  }

  const createTalentPool = async (payload) => {
    isLoading.value = true

    try {
      const response = await createCompanyTalentPool(payload)
      talentPools.value.unshift(response.data)
    } catch (error) {
      console.error('Error creating talent pool:', error)
    } finally {
      isLoading.value = false
    }
  }

  const updateTalentPool = async (id, payload) => {
    isLoading.value = true

    try {
      const { data } = await api.put(`/talent-pools/${id}`, payload)
      const index = talentPools.value.findIndex(p => p.id === id)

      if (index !== -1) {
        talentPools.value[index] = data
      }

      activePool.value = data
      return data
    } catch (error) {
      console.error('Failed to update talent pool:', error)
      return null
    }
  }

  const applyToTalentPool = async (talentPoolId) => {
    error.value = null
    actionLoadingId.value = talentPoolId

    // Find the pool in both possible states
    const poolInList = talentPools.value.find(p => p.id === talentPoolId);
    const poolIsActive = activePool.value && activePool.value.id === talentPoolId;

    // Determine the original status from whichever object is available
    const originalStatus = poolInList?.applicationStatus ?? activePool.value?.applicationStatus;

    // --- OPTIMISTIC UPDATE ---
    // Update the objects that actually exist
    if (poolInList) {
        poolInList.applicationStatus = 'Applied';
    }
    if (poolIsActive) {
        activePool.value.applicationStatus = 'Applied';
    }

    try {
      const response = await contractorApply(talentPoolId);

      if (response.data) {
        // --- SYNC UPDATE ---
        // Sync the objects that actually exist with the final server data
        if (poolInList) {
            Object.assign(poolInList, response.data);
        }
        if (poolIsActive) {
            Object.assign(activePool.value, response.data);
        }
      }

    } catch (err) {
      // --- REVERT ON ERROR ---
      // Revert the objects that actually exist
      if (poolInList) {
          poolInList.applicationStatus = originalStatus;
      }
      if (poolIsActive) {
          activePool.value.applicationStatus = originalStatus;
      }

      error.value = 'Failed to apply to the talent pool.';
      console.error(err);
    } finally {
      actionLoadingId.value = null
    }
  };

  const withdrawApplication = async (talentPoolId) => {
    error.value = null
    actionLoadingId.value = talentPoolId // Use the same loading state

    const poolInList = talentPools.value.find(p => p.id === talentPoolId);
    const poolIsActive = activePool.value && activePool.value.id === talentPoolId;

    // Store the original status from whichever object is available
    const originalStatus = poolInList?.applicationStatus ?? activePool.value?.applicationStatus;

    // --- OPTIMISTIC UPDATE ---
    if (poolInList) {
        poolInList.applicationStatus = null;
    }
    if (poolIsActive) {
        activePool.value.applicationStatus = null;
    }

    try {
      // 2. Make the DELETE request to your backend
      await contractorWithdraw(talentPoolId);
      // No need to sync on success, as we have already removed the status

    } catch (err) {
      // 3. REVERT ON ERROR ---
      if (poolInList) {
          poolInList.applicationStatus = originalStatus;
      }
      if (poolIsActive) {
          activePool.value.applicationStatus = originalStatus;
      }
      error.value = 'Failed to withdraw application.';
      console.error(err);
      // You should also show a user-facing error toast here
    } finally {
      actionLoadingId.value = null
    }
  };

  const getMembers = async (talentPoolId) => {
    isLoadingMembers.value = true
    error.value = null
    
    try {
      const response = await getCompanyTalentPoolMembers(talentPoolId)
      members.value = response.data
    } catch (err) {
      error.value = 'Failed to fetch projects.'
    } finally {
      isLoadingMembers.value = false
    }
  }

  const getMemberResume = async (talentPoolId, memberId) => {
  isLoading.value = true;
  error.value = null;

  try {
    const response = await getCompanyMemberResume(talentPoolId, memberId);


    // Get filename from Content-Disposition
    const disposition = response.headers['content-disposition'];
    let filename = 'resume.pdf';

    if (disposition) {
      const match = disposition.match(/filename="(.+)"/);
      if (match) filename = match[1];
    }

    // Create blob from raw PDF bytes
    const blob = new Blob([response.data], {
      type: response.headers['content-type'],
    });

    // Trigger download
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    window.URL.revokeObjectURL(url);

  } catch (err) {
    console.error(err);
    error.value = 'Failed to download member resume';
  } finally {
    isLoading.value = false;
  }
};

  const resetStore = () => {
    // Reset state to initial values
    talentPools.value = []
    workTypes.value = []
    isLoading.value = false
    actionLoadingId.value = null
    error.value = null
  }

  return {
    talentPools,
    members,
    activePool,
    workTypes,
    isLoading,
    isLoadingMembers,
    actionLoadingId,
    isLoadingActivePool,
    error,
    resetStore,
    fetchTalentPools,
    fetchTalentPoolById,
    fetchWorkTypes,
    createTalentPool,
    updateTalentPool,
    applyToTalentPool,
    withdrawApplication,
    fetchWorkTypes,
    getMembers,
    getMemberResume
  }
})
